import { createFileRoute, retainSearchParams, useParams, useNavigate, Link } from '@tanstack/react-router'
import { useSuspenseQuery, useSuspenseInfiniteQuery } from '@tanstack/react-query'
import { Suspense, useState, useDeferredValue, useMemo, type Ref } from 'react'
import { toast } from '@/lib/toast'
import { PlusIcon } from 'lucide-react'
import type { RootSearch } from '@/routes/__root'
import { trpc } from '@/main'
import { useLingui } from '@lingui/react/macro'
import { useIsMobile } from '@/hooks/use-mobile'
import { useHydrated } from '@/hooks/use-hydrated'
import { useSearchParamDialog } from '@/hooks/use-search-param-dialog'
import { usePermissions } from '@/hooks/use-permissions'
import { useRootSearch } from '@/hooks/use-root-search'
import { useInfiniteScroll } from '@/hooks/use-infinite-scroll'
import { useDebouncedSearch } from '@/hooks/use-debounced-search'
import { Button } from '@/components/ui/button'
import { SearchInput } from '@/components/ui/search-input'
import { Command, CommandDialog, CommandInput, CommandList, CommandEmpty, CommandItem } from '@/components/ui/command'
import { SectionLabel } from '@/components/custom/section-label'
import { NAVIGATION_ICONS } from './-components/shared/navigation-config'
import { AnimatedOutlet } from './-components/animated-outlet'
import { ListPanelBodySkeleton, MembersPanelSkeleton } from './-components/app-layout-skeleton'
import { ListPanePending } from './-components/shared/list-pane-pending'
import {
  PageLayout,
  StickyPanel,
  StickyHeader,
  ScrollableContent,
  ScrollableArea,
  SlideInPanel,
  CollapsibleListPanel,
  CollapsibleListPanelBorder,
} from './-components/shared/layout'
import {
  CollapsiblePanelProvider,
  CollapsiblePanel,
  CollapsiblePanelContent,
} from './-components/shared/collapsible-panel'
import { ScrollableList } from './-components/shared/scrollable-list'
import { SelectionHint, CollapsibleSectionLabel, CollapsibleToolbar } from './-components/shared/collapsible'
import { Avatar } from './-components/shared/avatar'
import { PanelEmptyState } from './-components/shared/panel-empty-state'
import { JobTreeItem } from './-components/jobs/job-tree-item'
import { JobsEmployeesPanel } from './-components/jobs/jobs-employees-panel'
import { JobActionMenu } from './-components/jobs/job-action-menu'
import { jobsListInput, jobsListInfiniteQueryOptions } from './-components/jobs/jobs-list-query'
import { ReportTabs } from './-components/report/report-tabs'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
// Dialogs — only mount when triggered by user (search-param or selection state).
import { JobDialog } from './-components/jobs/job-dialog'
import { DescribeProfileDialog } from './-components/offers/describe-profile-dialog'
import { EmployeeForm } from './-components/companies/employee-form'

const CRUMB_LINK_CLASS = 'text-foreground hover:text-foreground/70 text-base font-semibold sm:text-xl'

export const Route = createFileRoute('/_app/jobs')({
  search: {
    middlewares: [retainSearchParams<RootSearch>(true)],
  },
  loaderDeps: ({ search }) => ({ q: search.q }),
  loader: ({ context, deps }) => {
    context.queryClient.prefetchInfiniteQuery(jobsListInfiniteQueryOptions(jobsListInput(deps)))
  },
  component: RouteComponent,
  pendingComponent: ListPanePending,
})

function RouteComponent() {
  const { t } = useLingui()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  // Company-view (Block C): an admin/authorized employee in company view gets the company Jobs interface
  // (not their own assigned Position), so they're treated as not-an-employee-view here.
  const isCompanyView = usePermissions().isCompanyView
  const isEmployee = user?.type === 'applicant' && user.isEmployee && !isCompanyView

  // Access (logged-out → /login; self-registered professional → /report) is enforced centrally by the
  // `_app` layout guard (see route-access), before this route's gated `listJobs` query ever mounts.

  return (
    <div id="report" className="@container flex h-full flex-col">
      <PageLayout
        icon={NAVIGATION_ICONS.jobs}
        title={isEmployee ? t`Position` : t`Jobs`}
        breadcrumbsSlot={<JobsBreadcrumbs />}
        titleActions={<JobsTitleActions />}
      >
        <JobsLayout />
      </PageLayout>
    </div>
  )
}

function JobsLayout() {
  const { t } = useLingui()
  const { jobId } = useParams({ strict: false })
  const navigate = useNavigate()
  const [describeDialogOpen, setDescribeDialogOpen] = useState(false)
  const [dialogJobId, setDialogJobId] = useState<number | null>(null)
  // Job ids that should navigate to the report on describe-dialog close.
  const [postCreateNavigateJobs, setPostCreateNavigateJobs] = useState(new Set<number>())
  const jobDialog = useSearchParamDialog('editJob')
  const editJobIdFromSearch = typeof jobDialog.value === 'number' ? jobDialog.value : undefined

  // Employee edit dialog (triggered from position conflict dialog)
  const employeeDialog = useSearchParamDialog('editEmployee')
  const editEmployeeId = typeof employeeDialog.value === 'number' ? employeeDialog.value : undefined

  // Handle dialog close
  const handleDialogClose = (open: boolean) => {
    setDescribeDialogOpen(open)
    if (!open && dialogJobId) {
      const closedJobId = dialogJobId
      // Clear dialogJobId after a small delay to allow dialog to close gracefully
      setTimeout(() => setDialogJobId(null), 300)
      if (postCreateNavigateJobs.has(closedJobId)) {
        setPostCreateNavigateJobs((prev) => {
          const next = new Set(prev)
          next.delete(closedJobId)
          return next
        })
        toast.success(t`Saved successfully`)
        navigate({
          to: '/jobs/$jobId/report',
          params: { jobId: closedJobId.toString() },
        })
      }
    }
  }

  // Gate the viewport-driven auto-collapse on hydration: the panel body hydrates inside a Suspense
  // boundary, so flipping collapse from isMobile mid-hydration races that boundary and mismatches.
  const hydrated = useHydrated()
  const isMobile = useIsMobile() && hydrated

  const hasJobSelected = !!jobId

  return (
    <CollapsiblePanelProvider defaultCollapsed={isMobile && hasJobSelected}>
      <Suspense fallback={null}>
        {employeeDialog.mounted && (
          <EmployeeForm
            employeeId={editEmployeeId}
            open={employeeDialog.open}
            onOpenChange={employeeDialog.onOpenChange}
          />
        )}
        {dialogJobId != null && (
          <DescribeProfileDialog jobId={dialogJobId} open={describeDialogOpen} onOpenChange={handleDialogClose} />
        )}
        {jobDialog.mounted && (
          <JobDialog
            jobId={editJobIdFromSearch}
            open={jobDialog.open}
            onOpenChange={jobDialog.onOpenChange}
            onCreated={(newJobId) => {
              setPostCreateNavigateJobs((prev) => new Set(prev).add(newJobId))
              setDialogJobId(newJobId)
              setDescribeDialogOpen(true)
            }}
          />
        )}
      </Suspense>
      <div className="flex w-full overflow-hidden" style={{ containerType: 'inline-size' }}>
        {/* Left Panel - Jobs list (always visible, squashes to a 52px icon column when a job is
            selected; content keeps its full width inside and is clipped so only the avatars show) */}
        <CollapsibleListPanel isOpen={hasJobSelected} className="print:hidden">
          <CollapsibleListPanelBorder>
            <CollapsiblePanel expandedWidth="100%" disableTransition>
              {/* Single-list panel: the list view inside `CollapsiblePanelContent` is a `@container`
                  child of `SquashPanel`, so the same rendered list responsively collapses into a
                  centered avatar column (icon rail) at narrow widths via container queries. No dual
                  rendering — `JobsPanelContent` drives both states. */}
              <CollapsiblePanelContent>
                <Suspense fallback={<ListPanelBodySkeleton />}>
                  <JobsPanelContent variant="sidebar" />
                </Suspense>
              </CollapsiblePanelContent>
            </CollapsiblePanel>
          </CollapsibleListPanelBorder>
        </CollapsibleListPanel>

        {/* Right Panel Group (Middle + Detail) - width animates from 0 to target */}
        <SlideInPanel isOpen={hasJobSelected} innerClassName="flex">
          {/* Middle Panel - Integrants List (auto-collapsed on mobile) */}
          <CollapsiblePanelProvider cookieName="panel_state_middle" keyboardShortcut="]" defaultCollapsed={isMobile}>
            <StickyPanel className="print:hidden">
              <CollapsiblePanel>
                <Suspense fallback={<MembersPanelSkeleton />}>
                  <JobsEmployeesPanel />
                </Suspense>
              </CollapsiblePanel>
            </StickyPanel>
          </CollapsiblePanelProvider>

          {/* Right Panel - the `jobs.$jobId` layout route renders here via <AnimatedOutlet/>. It lives
              HERE (not in the `$jobId` layout) so the enter animation plays once when the report panel
              first appears and never re-animates on tab/job switches — see AnimatedOutlet. */}
          <ScrollableContent>
            <StickyHeader>
              <ReportTabs />
            </StickyHeader>
            <ScrollableArea>{hasJobSelected && <AnimatedOutlet className="h-full" />}</ScrollableArea>
          </ScrollableContent>
        </SlideInPanel>
      </div>
    </CollapsiblePanelProvider>
  )
}

function JobsBreadcrumbs() {
  const { t } = useLingui()
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const isCompanyView = usePermissions().isCompanyView
  const isEmployee = user?.type === 'applicant' && user.isEmployee && !isCompanyView
  const { jobId } = useParams({ strict: false })
  const sectionLabel = isEmployee ? t`Position` : t`Jobs`
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink
            className={CRUMB_LINK_CLASS}
            render={
              <Link
                to="/jobs"
                search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
              >
                {sectionLabel}
              </Link>
            }
          />
        </BreadcrumbItem>
        {jobId && (
          <Suspense fallback={null}>
            <JobDetailCrumb jobId={jobId} />
          </Suspense>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function JobDetailCrumb(props: { jobId: string }) {
  const { data: job } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(props.jobId) }))
  if (!job?.name) return null
  return (
    <>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbLink
          className={CRUMB_LINK_CLASS}
          render={
            <Link
              to="/jobs/$jobId/report"
              params={{ jobId: props.jobId }}
              search={(prev) => ({ ...prev, first: undefined, second: undefined, selected: undefined })}
            >
              {job.name}
            </Link>
          }
        />
      </BreadcrumbItem>
    </>
  )
}

function JobsTitleActions() {
  const { jobId } = useParams({ strict: false })
  if (!jobId) return null
  return (
    <Suspense fallback={null}>
      <JobsTitleActionsLoaded jobId={jobId} />
    </Suspense>
  )
}

function JobsTitleActionsLoaded(props: { jobId: string }) {
  const { data: job } = useSuspenseQuery(trpc.jobs.getJob.queryOptions({ jobId: Number(props.jobId) }))
  return job.viewerRole === 'owner' ? <JobActionMenu job={job} variant="header" /> : null
}

type PanelJob = {
  id: number
  name: string | null
  custom: { competences: boolean; values: boolean; personality: boolean; all: boolean }
}

// Reads the loader-prefetched `listJobs` infinite query (see this route's `loader`) and feeds the
// rail list view.
function JobsPanelContent(props: { variant?: 'sidebar' | 'main' }) {
  const { data: user } = useSuspenseQuery(trpc.auth.getUser.queryOptions())
  const isCompanyView = usePermissions().isCompanyView
  const isEmployee = user?.type === 'applicant' && user.isEmployee && !isCompanyView
  const { q: searchQueryFromUrl } = useRootSearch()
  const { searchInput, handleSearchChange } = useDebouncedSearch({ initialValue: searchQueryFromUrl })

  // Deferred input keeps the PREVIOUS list on screen while a new search page loads (keep-previous UX);
  // the staleness drives the search/progress spinner via `isFetching`.
  const input = useMemo(() => jobsListInput({ q: searchQueryFromUrl }), [searchQueryFromUrl])
  const deferredInput = useDeferredValue(input)
  const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useSuspenseInfiniteQuery(
    jobsListInfiniteQueryOptions(deferredInput),
  )
  // True while the deferred input lags the live one (a change is in flight) — keeps the search spinner /
  // fetching bar visible during the keep-previous transition.
  const isJobsFetching = isFetching || deferredInput !== input

  const { loadMoreRef } = useInfiniteScroll({
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    fetchNextPage,
  })

  const jobs = data?.pages.flatMap((page) => page.list) ?? []
  const totalCount = data?.pages[0]?.pagination.count ?? 0

  return (
    <JobsPanelView
      variant={props.variant}
      jobs={jobs}
      totalCount={totalCount}
      isEmployee={isEmployee}
      isFetching={isJobsFetching}
      searchInput={searchInput}
      onSearchChange={handleSearchChange}
      loadMoreRef={loadMoreRef}
      isFetchingMore={isFetchingNextPage}
      // The search term that produced the data currently on screen (the DEFERRED `q`, not the live
      // URL one). Empty-state decisions must key off this so clearing the search doesn't briefly flash
      // the cold "no items yet" state (live `q` is already '' while the stale search list is still shown).
      activeQuery={deferredInput.q}
    />
  )
}

function JobsPanelView(props: {
  variant?: 'sidebar' | 'main'
  jobs: PanelJob[]
  totalCount: number
  isEmployee: boolean
  isFetching: boolean
  searchInput: string
  onSearchChange: (value: string) => void
  loadMoreRef?: Ref<HTMLDivElement>
  isFetchingMore?: boolean
  activeQuery: string
}) {
  const { t } = useLingui()
  const isEmployee = props.isEmployee
  const allJobs = props.jobs
  const totalCount = props.totalCount
  const activeQuery = props.activeQuery
  const { jobId } = useParams({ strict: false })
  const { q: searchQueryFromUrl } = useRootSearch()
  const navigate = useNavigate()
  const [searchOpen, setSearchOpen] = useState(false)

  const handleCreateJob = () => {
    navigate({ to: '.', search: (prev) => ({ ...prev, editJob: 'new' }) })
  }
  // Create is owner-only (company account, or admin in company view); an `authorized` employee viewing
  // the company is read-only (the backend also rejects the write — Block A/C).
  const canCreateJobs = usePermissions().can('jobs:write')

  const showOnlyEmptyState = totalCount === 0 && !activeQuery

  // Employees have a single assigned position; companies pick from a list — different selection hint.
  const selectionHintText = isEmployee
    ? t`Click on the position to view its report`
    : t`Select a job to view its members and reports`

  const isMainVariant = props.variant === 'main'

  if (isMainVariant) {
    return (
      <div className="flex h-full flex-col">
        {/* Employees only ever have a single assigned position, so the search box is hidden for them. */}
        {!showOnlyEmptyState && !isEmployee && (
          <>
            {/* Search bar with add button */}
            <div className="flex items-center gap-2 border-b p-3">
              <SearchInput
                placeholder={t`Search jobs...`}
                value={props.searchInput}
                onChange={props.onSearchChange}
                isLoading={props.isFetching && searchQueryFromUrl.length > 0}
              />
              {!isEmployee && canCreateJobs && (
                <Button variant="secondary" onClick={handleCreateJob}>
                  <PlusIcon className="size-4" />
                  {t`Add`}
                </Button>
              )}
            </div>
          </>
        )}

        {/* Jobs list */}
        <ScrollableList
          isFetching={props.isFetching}
          loadMoreRef={props.loadMoreRef}
          isFetchingMore={props.isFetchingMore}
          isEmpty={allJobs.length === 0}
          emptyState={
            <PanelEmptyState
              noResults={allJobs.length === 0 && !!activeQuery}
              icon={<NAVIGATION_ICONS.jobs className="text-muted-foreground size-5" />}
              title={isEmployee ? t`No assigned position yet` : t`No jobs yet`}
              description={isEmployee ? t`Your company has not assigned you a position` : t`Add jobs to get started`}
              action={
                !isEmployee && canCreateJobs ? (
                  <Button variant="secondary" onClick={handleCreateJob}>
                    <PlusIcon className="size-4" />
                    {t`Add job`}
                  </Button>
                ) : undefined
              }
            />
          }
          hint={allJobs.length > 0 && <SelectionHint visible={!jobId} text={selectionHintText} />}
        >
          <SectionLabel className="px-2">
            {totalCount === 1 ? t`${totalCount} job` : t`${totalCount} jobs`}
          </SectionLabel>
          {allJobs.map((job) => (
            <JobTreeItem key={job.id} job={job} isSelected={jobId === job.id.toString()} hideMenu={isEmployee} />
          ))}
        </ScrollableList>
      </div>
    )
  }

  return (
    <>
      {/* Employees only ever have a single assigned position, so the search box is hidden for them. */}
      {!showOnlyEmptyState && !isEmployee && (
        <>
          <CollapsibleToolbar>
            <CollapsibleToolbar.Search onSearchClick={() => setSearchOpen(true)} searchLabel={t`Search jobs`}>
              <SearchInput
                placeholder={t`Search jobs...`}
                value={props.searchInput}
                onChange={props.onSearchChange}
                isLoading={props.isFetching && searchQueryFromUrl.length > 0}
              />
            </CollapsibleToolbar.Search>
            {!isEmployee && canCreateJobs && (
              <CollapsibleToolbar.Action
                icon={<PlusIcon className="size-4" />}
                label={t`Add`}
                tooltip={t`Add job`}
                onClick={handleCreateJob}
              />
            )}
          </CollapsibleToolbar>
        </>
      )}

      <ScrollableList
        isFetching={props.isFetching}
        loadMoreRef={props.loadMoreRef}
        isFetchingMore={props.isFetchingMore}
        isEmpty={allJobs.length === 0}
        emptyState={
          <PanelEmptyState
            noResults={allJobs.length === 0 && !!activeQuery}
            icon={<NAVIGATION_ICONS.jobs className="text-muted-foreground size-5" />}
            title={isEmployee ? t`No assigned position yet` : t`No jobs yet`}
            description={isEmployee ? t`Your company has not assigned you a position` : t`Add jobs to get started`}
            action={
              !isEmployee && canCreateJobs ? (
                <Button variant="secondary" onClick={handleCreateJob}>
                  <PlusIcon className="size-4" />
                  {t`Add job`}
                </Button>
              ) : undefined
            }
          />
        }
        hint={allJobs.length > 0 && <SelectionHint visible={!jobId} text={selectionHintText} />}
      >
        <CollapsibleSectionLabel>
          {totalCount === 1 ? t`${totalCount} job` : t`${totalCount} jobs`}
        </CollapsibleSectionLabel>
        {allJobs.map((job) => (
          <JobTreeItem key={job.id} job={job} isSelected={jobId === job.id.toString()} hideMenu={isEmployee} />
        ))}
      </ScrollableList>

      <CommandDialog open={searchOpen} onOpenChange={setSearchOpen} title={t`Search jobs`}>
        <Command className="gap-1">
          <CommandInput placeholder={t`Search jobs...`} />
          <CommandList>
            <CommandEmpty>{t`No jobs found`}</CommandEmpty>
            {allJobs.map((job) => (
              <CommandItem
                key={job.id}
                value={`${job.name ?? ''} ${job.id}`}
                onSelect={() => {
                  navigate({
                    to: '/jobs/$jobId/report',
                    params: { jobId: job.id.toString() },
                    search: (prev) => ({
                      ...prev,
                      first: { type: 'job', id: job.id },
                      second: undefined,
                      selected: 'first',
                    }),
                  })
                  setSearchOpen(false)
                }}
              >
                <Avatar size="sm" type="job" name={job.name} />
                <span className="truncate">{job.name}</span>
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </CommandDialog>
    </>
  )
}
